import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function useNotifications() {
  const { token, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (mountedRef.current) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      }
    } catch (e) {
      process.env.NODE_ENV === "development" && console.error("Failed to fetch notifications:", e.message);
    }
  }, [token]);

  const connectWS = useCallback(() => {
    if (!token || !user) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsProtocol = backendUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = backendUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "notification" && data.notification) {
            setNotifications((prev) => [data.notification, ...prev].slice(0, 50));
            setUnreadCount((prev) => prev + 1);
          }
        } catch (e) {
          process.env.NODE_ENV === "development" && console.error("WS message parse error:", e.message);
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (mountedRef.current && token) {
          reconnectTimer.current = setTimeout(connectWS, 5000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };

      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping");
        } else {
          clearInterval(pingInterval);
        }
      }, 30000);

      ws._pingInterval = pingInterval;
    } catch (e) {
      process.env.NODE_ENV === "development" && console.error("WS connection error:", e.message);
    }
  }, [token, user]);

  useEffect(() => {
    mountedRef.current = true;
    fetchNotifications();
    connectWS();

    return () => {
      mountedRef.current = false;
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };
  }, [fetchNotifications, connectWS]);

  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, read_by: [...(n.read_by || []), user?.id] }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      process.env.NODE_ENV === "development" && console.error("Failed to mark as read:", e.message);
    }
  }, [token, user]);

  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          read_by: [...new Set([...(n.read_by || []), user?.id])],
        }))
      );
      setUnreadCount(0);
    } catch (e) {
      process.env.NODE_ENV === "development" && console.error("Failed to mark all as read:", e.message);
    }
  }, [token, user]);

  return { notifications, unreadCount, markAsRead, markAllAsRead, refetch: fetchNotifications };
}
